import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Collapse,
  Container,
  IconButton,
  Drawer,
  Link,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useScrollTrigger
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import useWPMenu from "../../hooks/useWPMenu";
import useWPBranding from "../../hooks/useWPBranding";
import useWPSiteMeta from "../../hooks/useWPSiteMeta";
import useLanguage from "../../hooks/useLanguage";
import { useLocation } from "react-router-dom";
import { getLanguageSwitch, resolveLanguageSwitchHref } from "../../utils/langSwitch";
import DefaultSiteIcon from "./DefaultSiteIcon";
import { useThemePreset } from "../../styles/theme/ThemePresetContext";

type HeaderProps = {
  title?: string;
  ctaLabel?: string;
  ctaHref?: string;
};

const Header = ({ title = "MultiSide", ctaLabel = "Kontakt", ctaHref = "/kontakt" }: HeaderProps) => {
  const { lang, withLang } = useLanguage();
  const { preset, setPreset } = useThemePreset();
  const { items } = useWPMenu("primary", lang);
  const [open, setOpen] = useState(false);
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  const location = useLocation();
  const branding = useWPBranding();
  const meta = useWPSiteMeta();
  const elevated = useScrollTrigger({ disableHysteresis: true, threshold: 12 });
  const siteTitle = branding?.siteName || meta?.name || title;
  const siteSubtitle = meta?.description;
  const logoUrl = branding?.logo || meta?.site_icon_url;
  const resolvedCtaHref = lang === "en" && ctaHref === "/kontakt" ? "/contact" : ctaHref;
  const resolvedCtaLabel = lang === "en" && ctaLabel === "Kontakt" ? "Contact" : ctaLabel;
  const ctaLink = withLang(resolvedCtaHref);

  const currentPath = useMemo(() => {
    const path = location.pathname || "/";
    return path.endsWith("/") && path.length > 1 ? path.slice(0, -1) : path;
  }, [location.pathname]);

  const switchConfig = useMemo(() => getLanguageSwitch(currentPath, lang), [currentPath, lang]);
  const [switchHref, setSwitchHref] = useState(switchConfig.href);
  const [switchReady, setSwitchReady] = useState(false);

  useEffect(() => {
    let active = true;
    setSwitchReady(false);
    resolveLanguageSwitchHref(currentPath, lang)
      .then((resolved) => {
        if (!active) return;
        setSwitchHref(resolved);
        setSwitchReady(true);
      })
      .catch(() => {
        if (!active) return;
        setSwitchHref(switchConfig.href);
        setSwitchReady(true);
      });
    return () => {
      active = false;
    };
  }, [currentPath, lang, switchConfig.href]);

  const isActive = (href: string) => {
    if (!href || !href.startsWith("/")) return false;
    const normalized = href.endsWith("/") && href.length > 1 ? href.slice(0, -1) : href;
    return normalized === currentPath;
  };

  const toggleItem = (id: string) =>
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));

  const toggleThemePreset = () => setPreset(preset === "circuit" ? "atlas" : "circuit");
  const themeToggleTitle = preset === "circuit" ? "Zu Atlas wechseln" : "Zu Circuit wechseln";
  const themeToggleIcon =
    preset === "circuit" ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />;

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        boxShadow: elevated ? "0 10px 30px rgba(11, 31, 39, 0.12)" : "none",
        transition: "box-shadow 200ms ease"
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ py: 1, justifyContent: "space-between", gap: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {logoUrl ? (
              <Box component="img" src={logoUrl} alt={branding?.siteName || title} sx={{ height: 36 }} />
            ) : (
              <DefaultSiteIcon size={36} sx={{ color: "text.primary" }} label={siteTitle} />
            )}
            <Stack spacing={0}>
              <Typography variant="h6" component="div">
                {siteTitle}
              </Typography>
              {!branding?.logo && siteSubtitle ? (
                <Typography variant="caption" color="text.secondary">
                  {siteSubtitle}
                </Typography>
              ) : null}
            </Stack>
          </Stack>
          {items.length ? (
            <Stack
              direction="row"
              spacing={2}
              component="nav"
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              <Stack direction="row" spacing={2} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
                {items.map((item) => (
                  <Box
                    key={item.id}
                    component="li"
                    sx={{
                      position: "relative",
                      "&:hover > ul": { display: "block" },
                      "&:focus-within > ul": { display: "block" }
                    }}
                  >
                    <Link
                      href={item.url}
                      color="text.primary"
                      sx={{
                        fontWeight: isActive(item.url) ? 700 : 500,
                        pb: "2px",
                        transition: "box-shadow 180ms ease, color 180ms ease",
                        boxShadow: isActive(item.url) ? "inset 0 -2px 0 0" : "inset 0 -2px 0 0 transparent",
                        "&:hover": { boxShadow: "inset 0 -2px 0 0", color: "primary.main" }
                      }}
                    >
                      {item.title}
                    </Link>
                    {item.children?.length ? (
                      <Box
                        component="ul"
                        sx={{
                          listStyle: "none",
                          m: 0,
                          p: 1,
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          minWidth: 200,
                          bgcolor: "background.paper",
                          borderRadius: 2,
                          boxShadow: "0 12px 30px rgba(11, 31, 39, 0.18)",
                          border: "1px solid",
                          borderColor: "divider",
                          display: "none",
                          zIndex: 1200
                        }}
                      >
                        {item.children.map((child) => (
                          <Box component="li" key={child.id}>
                            <Link
                              href={child.url}
                              color="text.primary"
                              sx={{
                                display: "block",
                                px: 1.5,
                                py: 0.75,
                                borderRadius: 1.5,
                                "&:hover": { bgcolor: "rgba(13, 54, 64, 0.08)" }
                              }}
                            >
                              {child.title}
                            </Link>
                          </Box>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                ))}
              </Stack>
              <Link
                href={switchHref}
                underline="none"
                aria-label={switchConfig.label === "EN" ? "English version" : "German version"}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: "text.secondary",
                  bgcolor: "rgba(13, 54, 64, 0.04)",
                  transition: "border-color 180ms ease, color 180ms ease, background-color 180ms ease",
                  pointerEvents: switchReady ? "auto" : "none",
                  opacity: switchReady ? 1 : 0.75,
                  "&:hover": { color: "primary.main", borderColor: "primary.main", bgcolor: "rgba(13, 54, 64, 0.08)" }
                }}
              >
                {switchReady ? switchConfig.label : <CircularProgress size={14} thickness={5} color="inherit" />}
              </Link>
            </Stack>
          ) : null}
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={themeToggleTitle}>
              <IconButton
                onClick={toggleThemePreset}
                aria-label="Design umschalten"
                sx={{
                  display: { xs: "none", md: "inline-flex" },
                  width: 32,
                  height: 32,
                  border: "1px solid",
                  borderColor: "divider",
                  color: "text.secondary",
                  bgcolor: "rgba(13, 54, 64, 0.04)",
                  "&:hover": {
                    color: "primary.main",
                    borderColor: "primary.main",
                    bgcolor: "rgba(13, 54, 64, 0.08)"
                  }
                }}
              >
                {themeToggleIcon}
              </IconButton>
            </Tooltip>
            <Tooltip title={themeToggleTitle}>
              <IconButton
                onClick={toggleThemePreset}
                aria-label="Design umschalten"
                sx={{
                  display: { xs: "inline-flex", md: "none" },
                  width: 32,
                  height: 32,
                  border: "1px solid",
                  borderColor: "divider",
                  color: "text.secondary",
                  bgcolor: "rgba(13, 54, 64, 0.04)",
                  "&:hover": { color: "primary.main", borderColor: "primary.main", bgcolor: "rgba(13, 54, 64, 0.08)" }
                }}
              >
                {themeToggleIcon}
              </IconButton>
            </Tooltip>
            <Link
              href={switchHref}
              underline="none"
              aria-label={switchConfig.label === "EN" ? "English version" : "German version"}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "1px solid",
                borderColor: "divider",
                display: { xs: "inline-flex", md: "none" },
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "text.secondary",
                bgcolor: "rgba(13, 54, 64, 0.04)",
                transition: "border-color 180ms ease, color 180ms ease, background-color 180ms ease",
                pointerEvents: switchReady ? "auto" : "none",
                opacity: switchReady ? 1 : 0.75,
                "&:hover": { color: "primary.main", borderColor: "primary.main", bgcolor: "rgba(13, 54, 64, 0.08)" }
              }}
            >
              {switchReady ? switchConfig.label : <CircularProgress size={14} thickness={5} color="inherit" />}
            </Link>
            <Button
              variant="outlined"
              color="secondary"
              href={ctaLink}
              sx={{ display: { xs: "none", sm: "inline-flex" } }}
            >
              {resolvedCtaLabel}
            </Button>
            {items.length ? (
              <Button
                variant="text"
                color="inherit"
                onClick={() => setOpen(true)}
                sx={{ display: { xs: "inline-flex", md: "none" } }}
                startIcon={<MenuIcon />}
              >
                Menu
              </Button>
            ) : null}
          </Stack>
        </Toolbar>
      </Container>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 260, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Navigation
          </Typography>
          <List>
            {items.map((item) => {
              const isOpen = Boolean(openItems[item.id]);
              return (
                <Box key={item.id}>
                  <ListItem
                    disablePadding
                    secondaryAction={
                      item.children?.length ? (
                        <IconButton
                          edge="end"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            toggleItem(item.id);
                          }}
                          aria-label="Unterseiten"
                        >
                          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      ) : undefined
                    }
                  >
                    <ListItemButton
                      component="a"
                      href={item.url}
                      selected={isActive(item.url)}
                      onClick={() => setOpen(false)}
                      sx={{
                        borderLeft: "3px solid transparent",
                        transition: "border-color 180ms ease, color 180ms ease",
                        "&.Mui-selected": { borderColor: "primary.main", color: "primary.main" },
                        "&:hover": { borderColor: "primary.main", color: "primary.main" }
                      }}
                    >
                      <ListItemText primary={item.title} />
                    </ListItemButton>
                  </ListItem>
                  {item.children?.length ? (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {item.children.map((child) => (
                          <ListItem key={child.id} disablePadding sx={{ pl: 2 }}>
                            <ListItemButton component="a" href={child.url} onClick={() => setOpen(false)}>
                              <ListItemText primary={child.title} />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  ) : null}
                </Box>
              );
            })}
          </List>
          <Button variant="contained" color="primary" fullWidth href={ctaLink} onClick={() => setOpen(false)}>
            {resolvedCtaLabel}
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Header;
